import { HvacArea } from '@belimo-retrofit-portal/logic';
import { pipe } from 'fp-ts/function';
import * as A from 'fp-ts/ReadonlyArray';
import * as R from 'fp-ts/ReadonlyRecord';
import { createSelector } from 'reselect';
import { HVAC_AREA_MAP } from 'src/modules/hvac/constants/area';
import { getHvacAssesmentFormData } from 'src/modules/hvac/selectors/getHvacAssesmentFormData';
import { HvacFormCounter, HvacFormCounters } from 'src/modules/hvac/types/HvacFormCounter';
import {
  HvacFormAssessmentData,
  HvacFormSectionAnswerData,
} from 'src/modules/hvac/types/HvacFormData';

export const getHvacFormCounters = createSelector([
  getHvacAssesmentFormData,
], (formData): HvacFormCounters => pipe(
  HVAC_AREA_MAP,
  R.mapWithIndex((area) => getCounter(area, formData.groups)),
));

function getCounter(
  area: HvacArea,
  groups: HvacFormAssessmentData['groups'],
): HvacFormCounter {
  const sections = pipe(
    groups,
    R.toEntries,
    A.map(([, group]) => group),
    A.flatten,
    A.map((group) => group.areas[area]),
    A.map((areaData) => areaData.sections),
    A.flatten,
  );

  return {
    answered: sections.reduce((total, section) => {
      if (!section.enabled) {
        return total;
      }
      if (!section.config.has_impact) {
        return total;
      }

      const answer = isAnswered(section.answer) ? 1 : 0;
      return total + answer;
    }, 0),
    enabled: sections.reduce((total, section) => {
      if (!section.enabled) {
        return total;
      }
      if (!section.config.has_impact) {
        return total;
      }

      return total + 1;
    }, 0),
    total: sections.filter((section) => section.config.has_impact).length,
  };
}

function isAnswered(answer: HvacFormSectionAnswerData): boolean {
  return (
    answer.actual.option !== null &&
    answer.future.option !== null
  );
}
